import { createWebHistory, createRouter } from "vue-router";
import HomePage from "@/views/HomePage.vue";
import ViewAsset from "@/views/ViewAsset.vue";
import NewRequest from "@/views/NewRequest.vue";
import AppLayout from "@/AppLayout.vue";
import DashboardView from "@/views/DashboardView.vue";
import AssetLandingView from "@/views/AssetLandingView.vue";
import AnomalyManagementView from "@/views/AnomalyManagementView.vue";
import AdminView from "@/views/AdminView.vue"
import DualloAdminView from "@/views/DualloAdminView.vue"
import ProfileView from "@/views/ProfileView.vue"
import AssetAdmin from "@/views/AssetAdmin.vue";
import ScanAlignmentEditor from "@/views/ScanAlignmentEditor.vue";
import ProvenanceReviewView from "@/views/ProvenanceReviewView.vue";
import AuthLoading from "@/views/AuthLoading.vue";
import TermsAccept from "@/views/TermsAccept.vue";
import { Permissions } from "@/constants/permissions";
import { permissionGuard } from "./permissionGuard";
import { authGuard } from "@auth0/auth0-vue";
import ServiceUnavailable from "@/views/ServiceUnavailable.vue";
import ForbiddenPage from "@/views/Forbidden.vue";

const routes = [
    {
        path: "/",
        name: "Auth Loading",
        component: AuthLoading,
        beforeEnter: [authGuard]
    },
    {
        path: "/tos",
        name: "Terms of Service",
        component: TermsAccept,
        beforeEnter: [authGuard]
    },
    {
        path: '/service-unavailable',
        name: 'ServiceUnavailable',
        component: ServiceUnavailable,
        beforeEnter: [authGuard]
    },
    {
        path: '/forbidden',
        name: 'ForbiddenPage',
        component: ForbiddenPage,
        beforeEnter: [authGuard]
    },
    {
        path: "/",
        component: AppLayout,
        children: [
            {
                path: "/assets",
                name: "Assets",
                component: HomePage,
                beforeEnter: [authGuard]
            },
            {
                path: "/asset/:id",
                name: "AssetLanding",
                component: AssetLandingView,
                beforeEnter: [authGuard]
            },
            {
                path: "/asset/:id/viewer",
                name: "AssetViewer",
                component: ViewAsset,
                meta: {
                    permissions: [Permissions.ModelView]
                },
                beforeEnter: [authGuard, permissionGuard]
            },
            {
                path: "/request",
                name: "Request",
                component: NewRequest,
                beforeEnter: [authGuard]
            },
            {
                path: "/asset/:id/dashboard",
                name: "Dashboard",
                component: DashboardView,
                meta: {
                    permissions: [Permissions.ReportsView]
                },
                beforeEnter: [authGuard, permissionGuard]
            },
            {
                path: "/asset/:id/anomaly-management",
                name: "AnomalyManagement",
                component: AnomalyManagementView,
                meta: {
                    permissions: [Permissions.AnomalyView]
                },
                beforeEnter: [authGuard, permissionGuard]
            },
            {
                // Org-level entry: no asset in the route, so the view runs in org mode
                // (user picks a site/asset to scope). Same component + permissions.
                path: "/anomaly-management",
                name: "AnomalyManagementOrg",
                component: AnomalyManagementView,
                meta: {
                    permissions: [Permissions.AnomalyView]
                },
                beforeEnter: [authGuard, permissionGuard]
            },
            {
                path: "/admin",
                name: "Admin",
                component: AdminView,
                beforeEnter: [authGuard]
            },
            {
                path: "/duallo-admin",
                name: "DualloAdmin",
                component: DualloAdminView,
                meta: {
                    permissions: [Permissions.DualloAdminView]
                },
                beforeEnter: [authGuard, permissionGuard]
            },
            {
                path: "/profile",
                name: "Profile",
                component: ProfileView,
                beforeEnter: [authGuard]
            },
            {
                path: "/asset/:id/admin",
                name: "AssetAdmin",
                component: AssetAdmin,
                meta: {
                    permissions: [Permissions.AssetAdminView]
                },
                beforeEnter: [authGuard, permissionGuard]
            },
            {
                path: "/admin/ai-inference/scan/:scanId",
                name: "ProvenanceReview",
                component: ProvenanceReviewView,
                props: true,
                meta: {
                    permissions: [Permissions.DualloAdminView]
                },
                beforeEnter: [authGuard, permissionGuard]
            },
            {
                path: "/asset/:id/admin/model-version/:versionId/scans",
                name: "ModelVersionScans",
                component: ScanAlignmentEditor,
                props: true,
                meta: {
                    permissions: [Permissions.ModelVersionUpdate]
                },
                beforeEnter: [authGuard, permissionGuard]
            }
        ]
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    // The app scrolls an inner container (.scrollable-body in AppLayout), not the window,
    // so a returned window position is ignored — scroll that element to the top on navigation.
    scrollBehavior(to, from, savedPosition) {
        return new Promise((resolve) => {
            requestAnimationFrame(() => {
                const scroller = document.querySelector('.scrollable-body');
                if (scroller) {
                    scroller.scrollTo({ top: savedPosition ? savedPosition.top : 0, left: 0 });
                }
                resolve();
            });
        });
    },
});

export default router;